/**
 * Background Struggle Detection Utility
 * Analyzes student code using GPT-4 mini to detect struggle types
 * and logs them to the backend for future heatmap visualization.
 */

import { API_BASE_URL, apiFetch } from '../config';

/**
 * Analyzes student code in the background after tutor responds.
 * Uses GPT-4 mini to classify the type of struggle.
 * 
 * @param {string} username - Student username
 * @param {string} topic - Current topic/exercise being worked on
 * @param {string} code - The student's code
 * @param {Array} recentMessages - Last 10 messages from chat history
 */
export async function analyzeStruggleInBackground(username, topic, code, recentMessages) {
  // Fire and forget - don't await this in the calling code
  try {
    // Format recent messages for context
    // Exclude the last assistant message (tutor's response) to avoid confusion
    // The tutor's teaching content can mislead the analysis (e.g., mentions "functions" in explanation)
    // IMPORTANT: Filter out 'system' messages - they contain curriculum restrictions that would
    // confuse the struggle analysis (e.g., "Lists are in future chapter" would make it think
    // using lists is an error when it's actually valid Python)
    const filteredMessages = recentMessages
      .filter(msg => msg.role !== 'system') // Remove system prompts with curriculum lockdown
      .slice(-10);
    // Remove the last message if it's from the assistant (tutor's response)
    if (filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1]?.role === 'assistant') {
      filteredMessages.pop();
    }
    
    const chatContext = filteredMessages
      .map(msg => `${msg.role === 'user' ? 'Student' : 'Tutor'}: ${msg.content}`)
      .join('\n');

    const systemPrompt = `You are an expert Python instructor analyzing student code to detect struggle patterns.
Analyze the student's code and recent conversation to classify their struggle type.

IMPORTANT: Base your analysis on the conversation context - the tutor asked a question or gave an exercise, and this is the student's attempt.

Return ONLY a JSON object with these fields:
{
  "struggle_type": "SUCCESS" | "SYNTAX_ERROR" | "LOGIC_ERROR" | "RUNTIME_ERROR",
  "struggle_subtype": "<specific subtype or null>",
  "python_concept": "<generic Python concept being practiced>",
  "explanation": "<brief 1-2 sentence explanation>"
}

For python_concept, use ONE of these generic categories (pick the PRIMARY concept the student is working with):
- "input_output" (input(), print())
- "variables" (variable assignment, naming)
- "data_types" (int, float, str, type conversion)
- "arithmetic" (math operations, calculations)
- "strings" (string methods, formatting, f-strings)
- "conditionals" (if/elif/else)
- "loops_for" (for loops, range)
- "loops_while" (while loops)
- "lists" (list operations, indexing)
- "dictionaries" (dict operations)
- "functions" (def, parameters, return)
- "file_io" (file reading/writing)
- "error_handling" (try/except)
- "classes" (OOP, classes, objects)
- "modules" (import, libraries)
- "operators" (==, !=, and, or, not, comparison operators)
- "boolean" (True, False, boolean logic)
- "comments" (# comments, docstrings)
- "list_comprehension" ([x for x in ...])
- "tuples" (immutable sequences)
- "sets" (set operations, unique values)
- "slicing" (list[1:3], string slicing)
- "nested_loops" (loop inside loop)
- "recursion" (function calling itself)
- "lambda" (lambda functions)
- "sorting" (.sort(), sorted())
- "generators" (yield, generator expressions)
- "decorators" (@decorator)
- "context_managers" (with statement)
- "regex" (re module, pattern matching)
- "other" (if none of the above fit)

Subtypes for each type:
- SUCCESS: null (code works correctly for the given task)
- SYNTAX_ERROR subtypes:
  - "missing_colon" (forgot : after if/for/def/while/else)
  - "indentation" (wrong indentation level)
  - "unclosed_bracket" (missing ), ], or })
  - "unclosed_string" (missing quote)
  - "invalid_assignment" (e.g., 5 = x)
  - "missing_parentheses" (forgot () in function call)
  - "invalid_operator" (using = instead of ==)
  - "keyword_misuse" (using reserved word as variable)
  - "missing_comma" (forgot comma in list/dict/function args)
  - "extra_comma" (trailing comma causing error)
  - "invalid_escape" (bad escape sequence in string)
  - "unmatched_indent" (inconsistent tabs/spaces)
  - "invalid_character" (non-ASCII or invisible character)
  - "incomplete_statement" (line ends unexpectedly)
  - "typo" (misspelled variable, function, keyword, or method name)
  
- LOGIC_ERROR subtypes:
  - "wrong_operator" (used + instead of *, etc.)
  - "off_by_one" (loop goes one too many/few times)
  - "wrong_condition" (if condition is inverted or wrong)
  - "wrong_variable" (used wrong variable name)
  - "wrong_order" (statements in wrong sequence)
  - "missing_return" (function doesn't return value)
  - "wrong_return" (returns wrong value/type)
  - "infinite_loop" (loop never terminates)
  - "unreachable_code" (code after return/break)
  - "wrong_initialization" (variable initialized incorrectly)
  - "wrong_accumulator" (sum/count logic wrong)
  - "wrong_comparison" (comparing wrong things)
  - "wrong_index" (accessing wrong list/string position)
  - "wrong_slice" (slice bounds incorrect)
  - "missing_break" (forgot break in loop)
  - "missing_else" (missing else case)
  - "wrong_boolean_logic" (and/or/not used incorrectly)
  - "scope_confusion" (local vs global variable issue)
  - "mutation_error" (modifying list while iterating)
  - "incomplete_solution" (code is partial/unfinished)
  - "misunderstood_task" (solved different problem)
  - "hardcoded_values" (should use variables/input)
  - "wrong_formula" (mathematical formula is wrong)
  - "wrong_type_assumption" (assumed wrong data type)
  - "wrong_data_structure" (using list when dict needed, or vice versa)
  - "empty_body" (if/loop with no meaningful code, just pass)
  - "redundant_code" (unnecessary duplicate or dead logic)
  - "wrong_method" (e.g., .add() on list instead of .append())
  - "wrong_concatenation" (string + int without conversion)
  - "print_vs_return" (using print() when should return value)
  
- RUNTIME_ERROR subtypes:
  - "name_error" (undefined variable)
  - "type_error" (wrong type for operation)
  - "index_error" (list index out of range)
  - "key_error" (dict key not found)
  - "zero_division" (divide by zero)
  - "value_error" (invalid value for conversion)
  - "attribute_error" (object has no attribute)
  - "file_not_found" (file doesn't exist)
  - "import_error" (module not found)
  - "recursion_error" (max recursion depth)
  - "memory_error" (out of memory)
  - "overflow_error" (number too large)

If the code is correct and addresses what was asked, use SUCCESS.
If you cannot determine the specific subtype, pick the closest match from the list above.`;

    const userPrompt = `Recent conversation:
${chatContext}

Student's code:
\`\`\`python
${code}
\`\`\`

Analyze this code in the context of the conversation. What type of struggle (if any) is the student experiencing?`;

    const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0,
        max_tokens: 300
      })
    });

    if (!response.ok) {
      console.error('Struggle detection API error:', response.status);
      return;
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;

    if (!content) {
      console.error('No content in struggle detection response');
      return;
    }

    // Parse the JSON response
    let analysis;
    try {
      // Handle potential markdown code blocks in response
      const jsonStr = content.replace(/```json\n?|\n?```/g, '').trim();
      analysis = JSON.parse(jsonStr);
    } catch (parseError) {
      console.error('Failed to parse struggle detection response:', parseError);
      return;
    }

    // Log to backend
    const logPayload = {
      username: username || 'anonymous',
      topic: analysis.python_concept || 'other',
      code: code,
      struggle_type: analysis.struggle_type || 'LOGIC_ERROR',
      struggle_subtype: analysis.struggle_subtype || 'other',
      struggle_explanation: analysis.explanation || ''
    };

    const logResponse = await apiFetch(`${API_BASE_URL}/api/logStudentStruggle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(logPayload)
    });

    if (!logResponse.ok) {
      console.error('Failed to log struggle:', logResponse.status);
    } else {
      console.log('Struggle logged:', analysis.struggle_type, analysis.struggle_subtype);
    }

    // Award struggle points for successful code submissions
    if (analysis.struggle_type === 'SUCCESS' && username && username !== 'anonymous') {
      try {
        await apiFetch(`${API_BASE_URL}/api/points/award`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: username,
            actionType: 'struggle',
            subject: analysis.python_concept || 'other'
          })
        });
        console.log('Struggle points awarded for SUCCESS in:', analysis.python_concept);
      } catch (ptErr) {
        console.error('Failed to award struggle points:', ptErr);
      }
    }

  } catch (error) {
    // Silent fail - don't disrupt the main tutoring flow
    console.error('Background struggle detection error:', error);
  }
}
